'use client';

import { useState, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Copy, Key, Download, RefreshCw } from 'lucide-react';
import { useToast } from '@/hooks/use-toast';
import { ToolPageIntro } from '../../components/seo/ToolPageIntro';
import { InformationalContent } from '../../components/seo/InformationalContent';
import { rsaGeneratorContent } from '../../data/rsa-generator-content';

type KeyFormat = 'pkcs8' | 'spki' | 'jwk';
type KeyType = 'public' | 'private';

// Helper function to convert ArrayBuffer to Base64
function arrayBufferToBase64(buffer: ArrayBuffer): string {
    const bytes = new Uint8Array(buffer);
    let binary = '';
    for (let i = 0; i < bytes.byteLength; i++) {
        binary += String.fromCharCode(bytes[i]);
    }
    return btoa(binary);
}

// Helper function to format key with headers/footers
function formatKey(
    keyData: string,
    format: KeyFormat,
    keyType: KeyType
): string {
    const keyTypeStr = keyType === 'public' ? 'PUBLIC' : 'PRIVATE';
    const keyFormat = format === 'jwk' ? 'JSON' : format.toUpperCase();

    if (format === 'jwk') {
        return JSON.stringify(JSON.parse(keyData), null, 2);
    }

    return `-----BEGIN ${keyTypeStr} KEY-----
${keyData}
-----END ${keyTypeStr} KEY-----`;
}

export default function RsaGenerator() {
    const { toast } = useToast();
    const [keySize, setKeySize] = useState<2048 | 3072 | 4096>(2048);
    const [keyFormat, setKeyFormat] = useState<KeyFormat>('pkcs8');
    const [publicKey, setPublicKey] = useState<string>('');
    const [privateKey, setPrivateKey] = useState<string>('');
    const [isLoading, setIsLoading] = useState(false);
    const [showPrivateKey, setShowPrivateKey] = useState(false);

    // Generate RSA key pair
    const generateKeyPair = useCallback(async () => {
        setIsLoading(true);
        try {
            // Generate the key pair with RSASSA-PKCS1-v1_5 algorithm for better format compatibility
            const keyPair = await window.crypto.subtle.generateKey(
                {
                    name: 'RSASSA-PKCS1-v1_5',
                    modulusLength: keySize,
                    publicExponent: new Uint8Array([0x01, 0x00, 0x01]), // 65537
                    hash: 'SHA-256',
                },
                true, // extractable
                ['sign', 'verify']  // RSASSA-PKCS1-v1_5 is typically used for signing
            );

            // Export keys based on selected format
            if (keyFormat === 'jwk') {
                // For JWK format
                const [publicKeyData, privateKeyData] = await Promise.all([
                    window.crypto.subtle.exportKey('jwk', keyPair.publicKey),
                    window.crypto.subtle.exportKey('jwk', keyPair.privateKey)
                ]);
                setPublicKey(JSON.stringify(publicKeyData, null, 2));
                setPrivateKey(JSON.stringify(privateKeyData, null, 2));
            } else {
                // For PKCS#8 (private) and SPKI/PKCS#8 (public) formats
                const exportFormats = {
                    public: keyFormat === 'spki' ? 'spki' as const : 'pkcs8' as const,
                    private: 'pkcs8' as const
                };

                // Export the keys
                const [publicKeyData, privateKeyData] = await Promise.all([
                    window.crypto.subtle.exportKey(exportFormats.public, keyPair.publicKey)
                        .then((buffer: ArrayBuffer) => arrayBufferToBase64(buffer)),
                    window.crypto.subtle.exportKey(exportFormats.private, keyPair.privateKey)
                        .then((buffer: ArrayBuffer) => arrayBufferToBase64(buffer))
                ]);

                // Format the keys with appropriate headers/footers
                setPublicKey(formatKey(publicKeyData, keyFormat, 'public'));
                setPrivateKey(formatKey(privateKeyData, 'pkcs8', 'private'));
            }

            toast({
                title: 'Key pair generated',
                description: 'Your RSA key pair has been successfully generated.',
            });
        } catch (error) {
            console.error('Key generation error:', error);
            toast({
                title: 'Error',
                description: 'Failed to generate key pair. Please try again.',
                variant: 'destructive',
            });
        } finally {
            setIsLoading(false);
        }
    }, [keySize, keyFormat, toast]);

    // Copy key to clipboard
    const copyToClipboard = async (text: string, keyType: KeyType) => {
        try {
            await navigator.clipboard.writeText(text);
            toast({
                title: 'Copied!',
                description: `${keyType === 'public' ? 'Public' : 'Private'} key has been copied to clipboard.`,
            });
        } catch (error) {
            console.error('Copy error:', error);
            toast({
                title: 'Error',
                description: 'Failed to copy to clipboard.',
                variant: 'destructive',
            });
        }
    };

    // Download key as file
    const downloadKey = (content: string, keyType: KeyType) => {
        const element = document.createElement('a');
        const file = new Blob([content], { type: 'text/plain' });
        element.href = URL.createObjectURL(file);
        element.download = `${keyType}_key${keyFormat === 'jwk' ? '.json' : '.pem'}`;
        document.body.appendChild(element);
        element.click();
        document.body.removeChild(element);
    };

    return (
        <div className="container mx-auto px-4 py-8">
            <div className="max-w-4xl mx-auto">
                <ToolPageIntro title="RSA Key Pair Generator">
                    Generate RSA public and private key pairs. Set key size and format (PEM). Use for encryption, signing, or learning. Copy or download keys; no sign-up.
                </ToolPageIntro>

                <Card className="mb-6">
                    <CardHeader>
                        <CardTitle>Generate RSA Key Pair</CardTitle>
                        <CardDescription>
                            Generate a new RSA public/private key pair for encryption and decryption.
                        </CardDescription>
                    </CardHeader>
                    <CardContent>
                        <div className="space-y-6">
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                <div>
                                    <Label htmlFor="keySize" className="block mb-2">Key Size</Label>
                                    <select
                                        id="keySize"
                                        value={keySize}
                                        onChange={(e) => setKeySize(Number(e.target.value) as 2048 | 3072 | 4096)}
                                        className="w-full p-2 border rounded-md"
                                        disabled={isLoading}
                                    >
                                        <option value={2048}>2048 bits (Recommended)</option>
                                        <option value={3072}>3072 bits (High Security)</option>
                                        <option value={4096}>4096 bits (Maximum Security)</option>
                                    </select>
                                </div>

                                <div>
                                    <Label htmlFor="keyFormat" className="block mb-2">Key Format</Label>
                                    <select
                                        id="keyFormat"
                                        value={keyFormat}
                                        onChange={(e) => setKeyFormat(e.target.value as KeyFormat)}
                                        className="w-full p-2 border rounded-md"
                                        disabled={isLoading}
                                    >
                                        <option value="pkcs8">PKCS#8</option>
                                        <option value="spki">SPKI (Public Key Only)</option>
                                        <option value="jwk">JWK (JSON Web Key)</option>
                                    </select>
                                </div>
                            </div>

                            <div className="flex justify-end">
                                <Button
                                    onClick={generateKeyPair}
                                    disabled={isLoading}
                                >
                                    {isLoading ? (
                                        <>
                                            <RefreshCw className="mr-2 h-4 w-4 animate-spin" />
                                            Generating...
                                        </>
                                    ) : (
                                        <>
                                            <Key className="mr-2 h-4 w-4" />
                                            Generate Key Pair
                                        </>
                                    )}
                                </Button>
                            </div>

                            {publicKey && (
                                <div className="space-y-4">
                                    <div>
                                        <div className="flex items-center justify-between mb-2">
                                            <Label>Public Key</Label>
                                            <div className="space-x-2">
                                                <Button
                                                    variant="outline"
                                                    size="sm"
                                                    onClick={() => copyToClipboard(publicKey, 'public')}
                                                >
                                                    <Copy className="mr-2 h-3 w-3" />
                                                    Copy
                                                </Button>
                                                <Button
                                                    variant="outline"
                                                    size="sm"
                                                    onClick={() => downloadKey(publicKey, 'public')}
                                                >
                                                    <Download className="mr-2 h-3 w-3" />
                                                    Download
                                                </Button>
                                            </div>
                                        </div>
                                        <div className="relative">
                                            <pre className="p-4 bg-gray-50 dark:bg-gray-900 rounded-md overflow-auto text-xs">
                                                <code>{publicKey}</code>
                                            </pre>
                                        </div>
                                    </div>

                                    <div>
                                        <div className="flex items-center justify-between mb-2">
                                            <div className="flex items-center space-x-2">
                                                <Label>Private Key</Label>
                                                <div className="flex items-center space-x-1">
                                                    <Switch
                                                        id="show-private-key"
                                                        checked={showPrivateKey}
                                                        onCheckedChange={setShowPrivateKey}
                                                        className="h-4 w-8"
                                                    />
                                                    <Label htmlFor="show-private-key" className="text-xs">
                                                        {showPrivateKey ? 'Hide' : 'Show'}
                                                    </Label>
                                                </div>
                                            </div>
                                            <div className="space-x-2">
                                                <Button
                                                    variant="outline"
                                                    size="sm"
                                                    onClick={() => copyToClipboard(privateKey, 'private')}
                                                    disabled={!showPrivateKey}
                                                >
                                                    <Copy className="mr-2 h-3 w-3" />
                                                    Copy
                                                </Button>
                                                <Button
                                                    variant="outline"
                                                    size="sm"
                                                    onClick={() => downloadKey(privateKey, 'private')}
                                                    disabled={!showPrivateKey}
                                                >
                                                    <Download className="mr-2 h-3 w-3" />
                                                    Download
                                                </Button>
                                            </div>
                                        </div>
                                        <div className="relative">
                                            <pre className="p-4 bg-gray-50 dark:bg-gray-900 rounded-md overflow-auto text-xs">
                                                <code>{showPrivateKey ? privateKey : '••••••••••••••••••••••••••••••••'}</code>
                                            </pre>
                                            {!showPrivateKey && (
                                                <div className="absolute inset-0 flex items-center justify-center bg-black/10 dark:bg-white/5 backdrop-blur-sm rounded-md">
                                                    <span className="text-sm text-gray-500 dark:text-gray-400">
                                                        Click "Show" to reveal private key
                                                    </span>
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                </div>
                            )}
                        </div>
                    </CardContent>
                </Card>

                <Card className="bg-muted/50">
                    <CardHeader>
                        <CardTitle className="text-lg">About RSA Key Pairs</CardTitle>
                    </CardHeader>
                    <CardContent>
                        <div className="prose dark:prose-invert text-sm">
                            <p>
                                <strong>RSA (Rivest-Shamir-Adleman)</strong> is a public-key cryptosystem that is widely used for secure data transmission.
                                It is based on the practical difficulty of factoring the product of two large prime numbers.
                            </p>
                            <p className="mt-2">
                                <strong>Key Features:</strong>
                            </p>
                            <ul className="mt-1 space-y-1 list-disc pl-5">
                                <li><strong>Public Key:</strong> Used for encryption and can be shared openly</li>
                                <li><strong>Private Key:</strong> Used for decryption and must be kept secret</li>
                                <li><strong>Key Sizes:</strong> Larger keys provide more security but are slower to use</li>
                                <li><strong>Formats:</strong> Supports PKCS#8, SPKI, and JWK formats</li>
                            </ul>
                            <p className="mt-2 text-yellow-600 dark:text-yellow-400">
                                <strong>Security Note:</strong> Never share your private key. Anyone with access to it can decrypt messages intended for you.
                            </p>
                        </div>
                    </CardContent>
                </Card>

                {/* Informational Content */}
                <div className="mt-12">
                    <InformationalContent
                        title={rsaGeneratorContent.title}
                        description={rsaGeneratorContent.description}
                        content={rsaGeneratorContent.content}
                        faqs={rsaGeneratorContent.faqs}
                        relatedTools={rsaGeneratorContent.relatedTools}
                    />
                </div>
            </div>
        </div>
    );
}
